import React, { useState, useEffect, useCallback } from "react";
import { Modal, Input, List, message, Button } from "antd";
import { connect } from "react-redux";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { Locale, ApiManager, Util } from "../../../utils";
import { isBBShopMerchant } from "../../../utils/helpers/merchantHelpers";
import { ScopeSelectionFooter } from "./ScopeSelectionTags";
import "./coupons.scss";

const { Search } = Input;
const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const util = Util.getInstance();

function CategorySelectModal({
  visible,
  onCancel,
  user,
  categories,
  onSelect,
  disabled = false,
}) {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    const token = user?.token;
    if (!token) {
      return;
    }

    const categoryApi = isBBShopMerchant(user)
      ? api.product.categories
      : api.product.merchantCategories;

    try {
      setLoading(true);
      const res = await categoryApi.run({ token });
      if (res.result) {
        let list = [];
        if (isBBShopMerchant(user)) {
          list = res.data?.merchant_categories || [];
        } else {
          list = Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data)
              ? res.data
              : [];
        }
        setAllCategories(list);
      } else {
        message.error(res.message);
      }
    } catch (error) {
      console.log("CategorySelectModal -> error", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (visible) {
      loadCategories();
    }
  }, [visible, loadCategories]);

  const normalizedKeyword = keyword.trim().toLowerCase();
  const filteredCategories = allCategories.filter((item) => {
    if (!normalizedKeyword) {
      return true;
    }
    const zhName = (item.zh_name || "").toLowerCase();
    const ugName = (item.ug_name || "").toLowerCase();
    return (
      zhName.includes(normalizedKeyword) ||
      ugName.includes(normalizedKeyword) ||
      String(item.id).includes(normalizedKeyword)
    );
  });

  const checkIfSelected = (item) => {
    return categories && categories.items.indexOf(item.id) !== -1;
  };

  const onSelectItem = (item) => {
    if (disabled) {
      return;
    }

    if (checkIfSelected(item)) {
      const otherById = util.omit(categories.byId, [item.id]);
      const otherItems = categories.items.filter((id) => id !== item.id);
      onSelect({
        items: otherItems,
        byId: otherById,
      });
      return;
    }

    onSelect({
      items: [...categories.items, item.id],
      byId: {
        ...categories.byId,
        [item.id]: item,
      },
    });
  };

  const removeSelected = (id) => {
    if (disabled) {
      return;
    }
    onSelect({
      items: categories.items.filter((itemId) => itemId !== id),
      byId: util.omit(categories.byId, [id]),
    });
  };

  const clearSelected = () => {
    if (disabled) {
      return;
    }
    onSelect({ items: [], byId: {} });
  };

  return (
    <Modal
      className="coupon-scope-select-modal"
      width={720}
      title={
        <Search
          allowClear
          style={{ width: 360 }}
          value={keyword}
          placeholder={i18n.t("product.cats.name")}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={(value) => setKeyword(value)}
        />
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
    >
      <div className="coupon-scope-select-modal__list">
        <List
          itemLayout="horizontal"
          dataSource={filteredCategories}
          loading={loading}
          pagination={{
            pageSize: 8,
            showSizeChanger: false,
          }}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              actions={[
                <Button
                  key="select"
                  disabled={disabled}
                  type={checkIfSelected(item) ? "default" : "primary"}
                  onClick={() => onSelectItem(item)}
                >
                  {checkIfSelected(item)
                    ? i18n.t("base.modal.cancelText")
                    : i18n.t("sales.coupons.select")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={i18n.getPropNameByLocale(item, "name")}
                description={
                  item.products_count != null ? `${item.products_count}` : null
                }
              />
            </List.Item>
          )}
        />
      </div>
      <ScopeSelectionFooter
        selection={categories}
        disabled={disabled}
        onRemove={removeSelected}
        onClear={clearSelected}
      />
    </Modal>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(
  CategorySelectModal
);
